import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// API Configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PermissionsContext = createContext();

export const usePermissions = () => {
    const context = useContext(PermissionsContext);
    if (!context) {
        throw new Error('usePermissions muss innerhalb eines PermissionsProvider verwendet werden');
    }
    return context;
};

export const PermissionsProvider = ({ children, user }) => {
    const [permissions, setPermissions] = useState({
        dashboard: true,
        inventory: false,
        inventory_full: false,
        inventory_material_handover: false,
        fleet: false,
        employees: false,
        teams: false,
        calendar: false,
        calendar_own_teams: false,
        billing: false,
        vacation: false,
        vacation_own: false,
        analytics: false,
        settings: false
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user) {
            fetchPermissions();
        }
    }, [user]);

    const fetchPermissions = async () => {
        try {
            const response = await axios.get(`${API}/auth/permissions`);
            setPermissions(response.data.permissions);
        } catch (error) {
            console.error('Fehler beim Laden der Berechtigungen:', error);
            // Fallback: Basic permissions based on user type and role
            const fallbackPermissions = calculateFallbackPermissions(user);
            setPermissions(fallbackPermissions);
        } finally {
            setLoading(false);
        }
    };

    const calculateFallbackPermissions = (user) => {
        const basePermissions = {
            dashboard: true,
            inventory: false,
            inventory_full: false,
            inventory_material_handover: false,
            fleet: false,
            telecommunications: false,
            employees: false,
            teams: false,
            calendar: false,
            calendar_own_teams: false,
            billing: false,
            vacation: false,
            vacation_own: false,
            analytics: false,
            settings: false
        };

        if (user?.user_type === 'admin') {
            // Admin hat Vollzugriff
            Object.keys(basePermissions).forEach(key => {
                basePermissions[key] = true;
            });
            basePermissions.billing = true; // Explizit für Abrechnung
        } else if (user?.user_type === 'employee') {
            const role = (user?.role || 'techniker').toLowerCase();
            
            if (role === 'admin' || role === 'manager') {
                // Manager haben Vollzugriff
                Object.keys(basePermissions).forEach(key => {
                    basePermissions[key] = true;
                });
                basePermissions.billing = true; // Explizit für Abrechnung
            } else if (role === 'techniker' || role === 'technician') {
                // Techniker haben eingeschränkte Rechte - KEIN Dashboard-Zugriff
                basePermissions.dashboard = false;  // Dashboard deaktiviert für Techniker
                basePermissions.inventory = true;
                basePermissions.inventory_material_handover = true;
                basePermissions.calendar = true;
                basePermissions.calendar_own_teams = true;
                basePermissions.billing = true;  // Techniker können Abrechnungen erstellen
                basePermissions.vacation = true;
                basePermissions.vacation_own = true;
            }
        }

        console.log('🔐 Calculated permissions:', basePermissions);
        console.log('🔐 Billing permission:', basePermissions.billing);
        return basePermissions;
    };

    const hasPermission = (permission) => {
        return permissions[permission] === true;
    };

    const canAccess = (module) => {
        switch (module) {
            case 'dashboard':
                return hasPermission('dashboard');
            case 'inventory':
                return hasPermission('inventory');
            case 'fleet':
                return hasPermission('fleet');
            case 'telecommunications':
                return hasPermission('telecommunications');
            case 'employees':
                return hasPermission('employees');
            case 'teams':
                return hasPermission('teams');
            case 'calendar':
                return hasPermission('calendar');
            case 'billing':
                return hasPermission('billing');
            case 'vacation':
                return hasPermission('vacation');
            case 'analytics':
                return hasPermission('analytics');
            case 'settings':
                return hasPermission('settings');
            default:
                return false;
        }
    };

    const getUserRole = () => {
        if (user?.user_type === 'admin') {
            return 'admin';
        } else if (user?.user_type === 'employee') {
            return user?.role || 'techniker';
        }
        return 'unknown';
    };

    const isTechniker = () => {
        const role = user?.role?.toLowerCase() || '';
        return user?.user_type === 'employee' && (role === 'techniker' || role === 'technician');
    };

    const isAdminOrManager = () => {
        return user?.user_type === 'admin' || 
               (user?.user_type === 'employee' && (user?.role === 'admin' || user?.role === 'manager'));
    };

    const contextValue = {
        permissions,
        loading,
        hasPermission,
        canAccess,
        getUserRole,
        isTechniker,
        isAdminOrManager,
        user
    };

    return (
        <PermissionsContext.Provider value={contextValue}>
            {children}
        </PermissionsContext.Provider>
    );
};

export default PermissionsContext;